import { PREF_KEYS, readPref, writePref } from "@/lib/storage";

/**
 * Phase 48 — "hide amounts" (privacy): a per-device switch that masks every SAR figure as "SAR ••••" so a balance is
 * never on screen the moment someone signs in next to other people. It is a module flag read by `fmtSAR` (called
 * from plain functions, not only components); the shell re-mounts the page when it flips, so every figure follows.
 */
export const MASKED_AMOUNT = "SAR\u00A0••••";
let amountsHidden = readPref(PREF_KEYS.hideAmounts) === "1";
export const areAmountsHidden = () => amountsHidden;
export function setAmountsHidden(v) {
  amountsHidden = Boolean(v);
  writePref(PREF_KEYS.hideAmounts, amountsHidden ? "1" : "0");
}

/** "SAR 1,234.00" — the currency code and the amount are joined by a no-break space so they never wrap apart. */
export const fmtSAR = (v, digits = 2) => {
  if (amountsHidden) return MASKED_AMOUNT;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", currencyDisplay: "code", minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(Number(v) || 0)
    .replace("SAR", "SAR ")
    .replace(/\s+/g, "\u00A0");
};

export const fmtNum = (v, digits = 0) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(Number(v) || 0);

export const fmtPct = (v, digits = 2) => `${(Number(v) || 0).toFixed(digits)}%`;

/** Time units in milliseconds, for deadlines / countdowns / refresh intervals. */
export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

/**
 * Left-to-right mark. Dates keep their English form in the Arabic UI (user decision), but a leading number inside an
 * RTL paragraph would otherwise be re-ordered ("Sept 2026 13"). Prefixing an invisible LRM anchors "13 Sept 2026".
 */
export const LRM = "\u200E";

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return LRM + d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return LRM + d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const shortHash = (h, head = 10, tail = 6) => (h ? `${h.slice(0, head)}…${h.slice(-tail)}` : "—");

export const shortId = (id) => (id ? id.slice(0, 8) : "—");

export const timeLeft = (iso) => {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "ended";
  const d = Math.floor(diff / DAY_MS);
  const h = Math.floor((diff % DAY_MS) / HOUR_MS);
  if (d > 0) return `${LRM}${d}d ${h}h`;
  const m = Math.floor((diff % HOUR_MS) / MINUTE_MS);
  return `${LRM}${h}h ${m}m`;
};

/** Hours until `iso` (negative once passed; Infinity when missing) — drives the < 48 h vote-reminder emphasis. */
export const hoursLeft = (iso) => (iso ? (new Date(iso).getTime() - Date.now()) / HOUR_MS : Infinity);

export const URGENT_HOURS = 48;

/** True while an open deadline is less than 48 hours away (and not yet passed). */
export const isUrgent = (iso) => { const h = hoursLeft(iso); return h > 0 && h < URGENT_HOURS; };

export const toLocalInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
